import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  ActiveFlag,
  ActiveNote,
  ActiveTask,
  AppendEventInput,
  MobilisationEvent,
  Station,
} from '@hcc/types';
import { api } from '../lib/api';

export type PersonStatus = 'not_arrived' | 'on_site' | 'completed';
/**
 * Per-station completion record. Mirrors the API shape: `complete` is
 * the historical bool, `signature_key` is the latest KitIssued event's
 * S3 key (when one was captured) so the UI can pop a signature modal
 * from the Issued row without an extra fetch.
 */
export interface StationCompletionEntry {
  complete: boolean;
  signature_key: string | null;
  at: string | null;
  /** Event id of the latest KitIssued at this station, used to deep-link the receipt PDF. */
  latest_kit_issued_event_id: string | null;
}
export type StationCompletion = Record<Station, StationCompletionEntry>;

export type PreflightStatus = 'ok' | 'pending' | 'missing' | 'na';

/**
 * Coordinator-friendly summary of the pre-employment checklist for a
 * person. `missing` and `pending` are what gets chased; `ok` and
 * `na` are inert. Per-field nulls mean the metadata didn't include
 * that field, so the UI should treat it as "not yet set".
 */
export interface PreflightSummary {
  missing_count: number;
  pending_count: number;
  dbs: PreflightStatus | null;
  rtw: PreflightStatus | null;
  dvla: PreflightStatus | null;
  p45: PreflightStatus | null;
}

export interface PersonApi {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  employee_id: string | null;
  position: string | null;
  manager: string | null;
  contract: string | null;
  start_date: string | null;
  notes: string | null;
  arrived?: boolean;
  arrived_at?: string | null;
  /** Set when a LeftEarly event has been appended for this person. */
  left_early?: boolean;
  left_early_at?: string | null;
  left_early_reason?: string | null;
  /** ID of the still-active LeftEarly event, for the "Came back" undo flow. */
  left_early_event_id?: string | null;
  active_flags?: number;
  /**
   * Count of tasks whose latest derived status is not 'complete'
   * (pending + awaiting_review + changes_requested). Surfaced from
   * the people list so the coordinator can see who still owes
   * something at a glance.
   */
  outstanding_tasks?: number;
  /** Pre-employment checklist (DBS / right-to-work / DVLA / P45) summary from metadata. */
  preflight?: PreflightSummary;
  stations?: StationCompletion;
  /** S3 key of the latest reception photo, or null when no photo has been taken. */
  current_photo_s3_key?: string | null;
  /** True when the person is archived (hidden from live views by default). */
  archived?: boolean;
  created_at: string;
}

export interface PersonState {
  arrived: boolean;
  arrived_at: string | null;
  left_early: boolean;
  left_early_at: string | null;
  left_early_reason: string | null;
  flags: ActiveFlag[];
  notes: ActiveNote[];
  current_photo_s3_key: string | null;
  /** True when the person is currently archived. */
  archived: boolean;
  archived_at: string | null;
  archived_reason: string | null;
  /** Projected from task events; full list for admin views. */
  tasks: ActiveTask[];
}

const LIST_KEY = (
  q: string,
  arrived?: boolean,
  status?: PersonStatus,
  includeArchived?: boolean,
) => ['people', { q, arrived, status, includeArchived }] as const;
const ONE_KEY = (id: string) => ['people', id] as const;
const EVENTS_KEY = (id: string) => ['people', id, 'events'] as const;

export function usePeopleList(
  q: string,
  arrived?: boolean,
  status?: PersonStatus,
  includeArchived?: boolean,
) {
  return useQuery({
    queryKey: LIST_KEY(q, arrived, status, includeArchived),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (arrived !== undefined) params.set('arrived', String(arrived));
      if (status !== undefined) params.set('status', status);
      if (includeArchived) params.set('include_archived', 'true');
      const r = await api<{ people: PersonApi[] }>(
        `/api/people${params.size > 0 ? `?${params.toString()}` : ''}`,
      );
      return r.people;
    },
    // Keep the previous result on screen while a new search query is
    // in flight. Without this, every keystroke on the reception search
    // box clears the list and re-renders the skeleton — looks like a
    // flicker at typing speed. `isPlaceholderData` is true during the
    // refetch so we can dim the rows slightly if we want.
    placeholderData: keepPreviousData,
  });
}

export function usePerson(id: string | null) {
  return useQuery({
    queryKey: id ? ONE_KEY(id) : ['people', 'none'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api<{ person: PersonApi; state: PersonState }>(
        `/api/people/${id}`,
      );
      return r;
    },
  });
}

export function usePersonEvents(id: string | null) {
  return useQuery({
    queryKey: id ? EVENTS_KEY(id) : ['people', 'none', 'events'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api<{ events: MobilisationEvent[] }>(
        `/api/people/${id}/events`,
      );
      return r.events;
    },
  });
}

export function useAppendEvent(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AppendEventInput) => {
      const r = await api<{ event: MobilisationEvent }>(
        `/api/people/${personId}/events`,
        { method: 'POST', body: JSON.stringify(input) },
      );
      return r.event;
    },
    onSuccess: () => {
      // Invalidate any list queries (whatever filter is active) and
      // this person's projection so the reception list refreshes.
      qc.invalidateQueries({ queryKey: ['people'] });
      // The outstanding-kit seed depends on issued events, and the
      // socket broadcast is not guaranteed to arrive (socket down,
      // HTTP up); invalidate locally so a re-opened drawer never
      // seeds from the pre-issue entitlement.
      qc.invalidateQueries({ queryKey: ['station-kit', personId] });
    },
  });
}

/**
 * Archive or unarchive a person. Archiving hides them from the live
 * on-day views (reception, stations, coordinator) while keeping every
 * record and report intact; unarchiving brings them back. Reversible,
 * audited on the event chain.
 */
export function useArchivePerson(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      archive,
      reason,
    }: {
      archive: boolean;
      reason?: string;
    }) => {
      await api(
        `/api/admin/people/${personId}/${archive ? 'archive' : 'unarchive'}`,
        { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/**
 * Permanently delete a person. Irreversible; admin-only on the server.
 * For test or erroneous records only — real attendees should be
 * anonymised (pseudonymise) instead, which keeps the audit chain.
 */
export function useDeletePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (personId: string) => {
      await api(`/api/admin/people/${personId}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}
